<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { NModal, NSpin, NTooltip, useMessage } from "naive-ui";
import { useRouter } from "vue-router";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { ProperSelect } from "@propertechnologies/ajiaco";
import {
    finishOnboarding,
    getCurrentStepRoutePath,
    getOnboardingViewSteps,
    getOrderedSteps,
    isOnboardingInProgress,
    loadOnboardingProgressFromBackend,
    onboardingProgress,
    setCurrentStep,
    setPendingStep,
    skipStep,
} from "../constants/onboardingProgress";
import { CallToActionType } from "../models/onboarding_steps.ts";
import { getPmsOptions, updatePlatform } from "../services/user";
import { useOrganization } from "../hooks/useOrganization";
import { useOnboardingVideo } from "../hooks/useOnboardingVideo";
import YoutubePlayer from "../components/onboarding/YoutubePlayer.vue";
import { useAccountingLead } from "../hooks/useAccountingLead";

const router = useRouter();
const message = useMessage();

const showModal = ref(false);
const modalStep = ref(1);
const selectedPms = ref<string | null>(null);

const { organization } = useOrganization();
const { playVideo } = useOnboardingVideo();
const { accountingLead } = useAccountingLead();

const { isLoading } = useQuery({
    queryKey: ["onboarding-progress"],
    queryFn: loadOnboardingProgressFromBackend,
    staleTime: 0,
});

const { data: pmsOptionsRaw, isLoading: isLoadingPmsOptions } = useQuery({
    queryKey: ["pms-options"],
    queryFn: getPmsOptions,
});

const pmsOptions = computed(() =>
    (pmsOptionsRaw.value ?? []).map((pms) => ({ label: pms, value: pms })),
);

// Initialize selectedPms from organization once loaded
watch(
    () => organization.value?.platform,
    (platform) => {
        if (platform && !selectedPms.value) {
            selectedPms.value = platform;
        }
    },
    { immediate: true },
);

const { mutate: savePlatform, isPending: isSavingPlatform } = useMutation({
    mutationFn: (platform: string) => updatePlatform(platform),
    onError: () => {
        message.error("Failed to update PMS");
    },
});

function onPmsChange(value: string) {
    selectedPms.value = value;
    if (value) {
        savePlatform(value);
    }
}

// Steps shown in the onboarding wizard (excludes OPEN_ITEMS)
const onboardingViewSteps = computed(() => getOnboardingViewSteps());
// All steps including OPEN_ITEMS (for allStepsResolved check)
const orderedSteps = computed(() => getOrderedSteps());

const currentStep = computed(() => {
    const match = onboardingViewSteps.value.find(
        (step) => step.step_code === onboardingProgress.currentStepCode,
    );
    // When currentStepCode is OPEN_ITEMS (not in wizard), show the last wizard step
    return match ?? onboardingViewSteps.value[onboardingViewSteps.value.length - 1];
});

const currentStepIndex = computed(() =>
    onboardingViewSteps.value.findIndex((step) => step.step_code === currentStep.value?.step_code),
);

const { mutateAsync: skipCurrentStep, isPending: isSkipping } = useMutation({
    mutationFn: async () => {
        if (!currentStep.value) {
            return;
        }

        await skipStep(currentStep.value.step_code);
    },
});

const { mutateAsync: setPmsPending, isPending: isSettingPending } = useMutation({
    mutationFn: async () => {
        await setPendingStep("PMS");
    },
});

const isMutatingStep = computed(() => isSkipping.value || isSettingPending.value);

const isCurrentStepResolved = computed(
    () => currentStep.value?.status === "completed" || currentStep.value?.status === "skipped",
);

const allStepsResolved = computed(
    () =>
        orderedSteps.value.length > 0 &&
        orderedSteps.value.every(
            (step) => step.status === "completed" || step.status === "skipped",
        ),
);

const isPmsStepPending = computed(() => {
    const pmsStep = orderedSteps.value.find((s) => s.step_code === "PMS");
    return pmsStep?.status === "pending";
});

const mainCtaText = computed(() => {
    if (!currentStep.value) return "";
    if (currentStep.value.status === "completed") {
        return `${currentStep.value.main_call_to_action_text}`;
    }
    return currentStep.value.main_call_to_action_text;
});

function indicatorClass(stepCode: string) {
    if (!allStepsResolved.value && currentStep.value?.step_code === stepCode) {
        return "bg-purple-200";
    }

    return "bg-cream-dark";
}

function onClickStep(stepCode: string) {
    setCurrentStep(stepCode);
}

function onBack() {
    if (currentStepIndex.value <= 0) {
        return;
    }

    const previousStep = onboardingViewSteps.value[currentStepIndex.value - 1];
    setCurrentStep(previousStep.step_code);
}

function onNext() {
    if (currentStepIndex.value >= onboardingViewSteps.value.length - 1) {
        // Last wizard step — next is the OPEN_ITEMS step shown on /open-items
        const openItemsStep = orderedSteps.value.find((s) => s.step_code === "OPEN_ITEMS");
        if (
            openItemsStep &&
            openItemsStep.status !== "completed" &&
            openItemsStep.status !== "skipped"
        ) {
            setCurrentStep("OPEN_ITEMS");
            router.push("/open-items");
        }
        return;
    }

    const nextStep = onboardingViewSteps.value[currentStepIndex.value + 1];
    setCurrentStep(nextStep.step_code);
}

async function onSkip() {
    await skipCurrentStep();
    if (onboardingProgress.currentStepCode === "OPEN_ITEMS" && isOnboardingInProgress()) {
        router.push("/open-items");
    }
}

async function onMainAction() {
    if (!currentStep.value) {
        return;
    }

    if (currentStep.value.main_call_to_action_type === CallToActionType.Modal) {
        modalStep.value = 1;
        showModal.value = true;
        return;
    }

    // Sync currentStepCode with the displayed step so routing works correctly
    setCurrentStep(currentStep.value.step_code);

    const allowedPath = getCurrentStepRoutePath();
    if (!allowedPath) {
        return;
    }

    await router.push(allowedPath.pathname + allowedPath.search + allowedPath.hash);
}

const { mutateAsync: goHome, isPending: isFinishing } = useMutation({
    mutationFn: async () => {
        await finishOnboarding();
    },
    onSuccess: () => {
        router.push("/");
    },
});

async function onGoHome() {
    await goHome();
}

const hasPmsSet = computed(() => !!selectedPms.value);

async function onModalNext() {
    if (modalStep.value === 1) {
        modalStep.value = 2;
    } else if (modalStep.value === 2) {
        await setPmsPending();
        modalStep.value = 3;
    }
}

function onModalBack() {
    if (modalStep.value > 1) {
        modalStep.value = modalStep.value - 1;
    }
}

function copyEmail() {
    const email = organization.value?.props?.general_team_email;
    if (email) {
        navigator.clipboard.writeText(email);
        message.info("Email copied to clipboard");
    }
}
</script>

<template>
    <main
        class="flex flex-col items-center justify-center gap-4 bg-cream p-4 min-h-screen h-auto overflow-y-auto"
    >
        <template v-if="isLoading">
            <n-spin size="large" />
            <p class="text-[16px]">Loading your onboarding progress...</p>
        </template>

        <template v-else>
            <h1 class="text-4xl font-semibold">
                {{ allStepsResolved ? "You're Done!" : "Welcome to Proper!" }}
            </h1>
            <p v-if="!allStepsResolved" class="text-[16px]">Complete steps to get started</p>

            <div class="flex items-center flex-wrap gap-2">
                <n-tooltip
                    v-for="step in onboardingViewSteps"
                    :key="step.step_code"
                    trigger="hover"
                >
                    <template #trigger>
                        <button
                            class="flex items-center justify-center h-10 w-10 rounded-lg"
                            :class="indicatorClass(step.step_code)"
                            :disabled="isMutatingStep || allStepsResolved"
                            @click="onClickStep(step.step_code)"
                        >
                            <span
                                v-if="step.status === 'completed'"
                                class="material-symbols-outlined text-[18px] text-green-800"
                            >
                                check_circle
                            </span>
                            <span
                                v-else-if="step.status === 'pending'"
                                class="material-symbols-outlined text-[18px] text-orange-500"
                            >
                                history
                            </span>
                            <span
                                v-else-if="step.status === 'skipped'"
                                class="material-symbols-outlined text-[18px] text-orange-500"
                            >
                                info
                            </span>
                            <span
                                v-else
                                :class="{
                                    'text-gray-400':
                                        onboardingProgress.currentStepCode !== step.step_code,
                                }"
                            >
                                {{ step.step_number }}
                            </span>
                        </button>
                    </template>
                    Step {{ step.step_number }} - {{ step.title }}
                </n-tooltip>
            </div>

            <div class="w-full max-w-2xl bg-white rounded-lg overflow-hidden">
                <div v-if="!allStepsResolved" class="w-full relative">
                    <div
                        class="absolute top-4 left-4 z-10 flex items-center backdrop-blur-md bg-white/30 rounded-[4px] px-4 py-2 border border-white/30"
                    >
                        <p class="leading-none font-semibold">
                            Step {{ currentStep?.step_number }} of
                            {{ onboardingViewSteps.length + 1 }}
                        </p>
                    </div>
                    <youtube-player
                        v-if="currentStep?.video_path"
                        :key="currentStep.step_code"
                        :video-id="currentStep.video_path"
                        :poster-url="currentStep.image_path"
                        @play="
                            playVideo(
                                currentStep!.video_path!,
                                currentStep!.title,
                                currentStep!.image_path,
                            )
                        "
                    />
                    <div
                        v-else-if="currentStep?.image_path"
                        class="bg-gray-500 w-full aspect-video"
                    >
                        <img
                            :src="currentStep.image_path"
                            :alt="currentStep.title"
                            class="w-full h-full object-cover"
                        />
                    </div>
                </div>

                <div class="p-6 flex flex-col justify-center items-center gap-2">
                    <template v-if="allStepsResolved">
                        <h2 class="text-xl font-semibold">Next Steps: Go Home</h2>
                        <p class="max-w-md text-center text-[16px]">Check out your home page</p>

                        <div class="w-full flex items-center justify-center gap-4 mt-3">
                            <button class="button" :disabled="isFinishing" @click="onGoHome">
                                Go to Home
                            </button>
                        </div>
                    </template>

                    <template v-else>
                        <h2 class="text-xl font-semibold">{{ currentStep?.title }}</h2>
                        <p class="max-w-md text-center text-[16px]">
                            {{ currentStep?.description }}
                        </p>

                        <div class="w-full flex items-center justify-center gap-4 mt-3">
                            <button
                                v-if="currentStepIndex > 0"
                                class="text-label flex items-center gap-1 active:scale-95 active:filter active:brightness-90"
                                :disabled="isMutatingStep"
                                @click="onBack"
                            >
                                <span class="material-symbols-outlined text-base">arrow_back</span>
                                Back
                            </button>

                            <button
                                :class="isCurrentStepResolved ? 'secondary-button' : 'button'"
                                :disabled="isMutatingStep"
                                @click="onMainAction"
                            >
                                {{ mainCtaText }}
                            </button>

                            <button
                                v-if="isCurrentStepResolved"
                                class="button"
                                :disabled="isMutatingStep"
                                @click="onNext"
                            >
                                Next
                                <span class="material-symbols-outlined text-base"
                                    >arrow_forward</span
                                >
                            </button>

                            <button
                                v-else-if="currentStep?.is_skippable"
                                class="text-label active:scale-95 active:filter active:brightness-90"
                                :disabled="isMutatingStep"
                                @click="onSkip"
                            >
                                Skip
                            </button>
                        </div>
                    </template>
                </div>
            </div>

            <n-modal
                v-model:show="showModal"
                preset="card"
                :style="{
                    width: '600px',
                }"
                title="Connect Your PMS"
                :bordered="false"
                size="medium"
            >
                <div class="flex flex-col gap-4">
                    <!-- Step indicators -->
                    <div class="flex items-center w-full">
                        <template
                            v-for="({ number, label }, index) in [
                                { number: 1, label: 'Select PMS' },
                                { number: 2, label: 'Invite Us' },
                                { number: 3, label: 'Confirm' },
                            ]"
                            :key="number"
                        >
                            <div class="flex items-center gap-1 sm:gap-2 shrink-0">
                                <div
                                    class="flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 shrink-0 rounded-full text-[10px] sm:text-xs"
                                    :class="
                                        modalStep === number
                                            ? 'bg-purple-300'
                                            : 'bg-white border border-gray-300 text-gray-400'
                                    "
                                >
                                    {{ number }}
                                </div>
                                <span
                                    class="text-[10px] sm:text-[12px]"
                                    :class="modalStep === number ? 'text-black' : 'text-gray-400'"
                                >
                                    {{ label }}
                                </span>
                            </div>
                            <div
                                v-if="index < 2"
                                class="flex-1 h-px bg-gray-300 mx-1 sm:mx-3 min-w-2"
                            />
                        </template>
                    </div>

                    <!-- Step 1: Select PMS -->
                    <div v-if="modalStep === 1" class="flex flex-col gap-3">
                        <p>Select your Property Management System</p>
                        <ProperSelect
                            :model-value="selectedPms"
                            :options="pmsOptions"
                            :loading="isLoadingPmsOptions || isSavingPlatform"
                            :disabled="isPmsStepPending"
                            placeholder="Connect PMS"
                            @update:model-value="(value) => onPmsChange(value as string)"
                        />

                        <div class="flex justify-end gap-2 mt-4">
                            <button class="secondary-button" @click="showModal = false">
                                Cancel
                            </button>
                            <button class="button" :disabled="!hasPmsSet" @click="onModalNext">
                                Next
                            </button>
                        </div>
                    </div>

                    <!-- Step 2: Invite Us -->
                    <div v-else-if="modalStep === 2" class="flex flex-col gap-3">
                        <p>Invite Proper to your PMS</p>
                        <p>
                            Send an invite to the email below. a Proper team member will accept the
                            invite and connect your PMS.
                        </p>
                        <div class="bg-cream rounded-md p-4">
                            <div class="border-b border-gray-200 pb-4">
                                <p class="text-label !text-[10px] text-gray-400">Email</p>
                                <p class="mt-1 flex items-center gap-1 justify-between">
                                    <span>{{
                                        organization?.props?.general_team_email || "-"
                                    }}</span>
                                    <button
                                        v-if="organization?.props?.general_team_email"
                                        class="inline-flex items-center text-gray-400 hover:text-gray-600 active:scale-95"
                                        @click="copyEmail"
                                    >
                                        <span class="material-symbols-outlined text-[18px]">
                                            content_copy
                                        </span>
                                    </button>
                                </p>
                            </div>

                            <div class="flex gap-3 border-b border-gray-200 pb-4 my-4">
                                <div class="w-1/2">
                                    <p class="text-label !text-[10px] text-gray-400">First Name</p>
                                    <p class="mt-1">Proper</p>
                                </div>
                                <div class="w-1/2">
                                    <p class="text-label !text-[10px] text-gray-400">Last Name</p>
                                    <p class="mt-1">Team</p>
                                </div>
                            </div>

                            <div class="pb-4">
                                <p class="text-label !text-[10px] text-gray-400">Role</p>
                                <p class="mt-1">Accountant</p>
                            </div>
                        </div>

                        <div class="flex justify-end gap-2 mt-4">
                            <button class="secondary-button" @click="onModalBack">Back</button>
                            <button
                                class="button"
                                :disabled="
                                    isMutatingStep || !organization?.props?.general_team_email
                                "
                                @click="onModalNext"
                            >
                                Next
                            </button>
                        </div>
                    </div>

                    <!-- Step 3: Confirmation -->
                    <div v-else-if="modalStep === 3" class="flex flex-col gap-3">
                        <p class="text-center py-4">
                            Thank you! We'll review your PMS connection and be in touch with you
                            shortly.
                        </p>

                        <div class="flex justify-end mt-4">
                            <button class="secondary-button" @click="showModal = false">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </n-modal>

            <p class="text-[16px]">
                Need Help? Talk to your
                <a
                    v-if="accountingLead?.email"
                    :href="`mailto:${accountingLead.email}`"
                    class="font-semibold underline hover:cursor-pointer"
                >
                    Proper Rep
                </a>
                <span v-else class="font-semibold underline hover:cursor-pointer">Proper Rep</span>
            </p>
        </template>
    </main>
</template>

<style scoped></style>
